// Configuración de la PWA leída de variables de entorno de build (Vite).
//
// Los valores reales se inyectan en el build de despliegue (Fase 5/Tarea 21):
// URL de la API (API Gateway) e identificadores de Cognito. Para desarrollo
// local se pueden definir en un archivo `.env.local`.

/** Configuración resuelta de la app. */
export interface AppConfig {
  /** URL base de la API REST (API Gateway). */
  apiUrl: string;
  /** Región AWS del User Pool de Cognito. */
  cognitoRegion: string;
  /** Id del User Pool de Cognito. */
  cognitoUserPoolId: string;
  /** Id del cliente (App client) del User Pool. */
  cognitoClientId: string;
  /**
   * Clave PÚBLICA de Stripe (pk_...). No es secreta: identifica la cuenta en el
   * cliente. Solo se usa si en el futuro se integra Stripe.js/Elements; el flujo
   * actual con Checkout alojado redirige a Stripe y no la necesita en el cliente.
   */
  stripePublishableKey: string;
  /**
   * Measurement ID de Google Analytics 4 (formato `G-XXXXXXXXXX`). No es
   * secreto: identifica la propiedad en el cliente. Si está vacío, GA4 no se
   * carga (degradación elegante).
   */
  gaMeasurementId: string;
  /**
   * Tag ID de Contentsquare (identificador hex de 13 caracteres, p. ej.
   * `df76f9b023a0c`). Es la plataforma de Session Replay / Heatmaps (antes
   * Hotjar). No es secreto. Si está vacío, el tag no se carga (degradación
   * elegante).
   */
  contentsquareTagId: string;
  /**
   * Id de Google Ads (formato `AW-XXXXXXXXXX`). No es secreto: identifica la
   * cuenta de Ads en el cliente para medir conversiones/remarketing. Comparte el
   * mismo gtag.js/dataLayer que GA4. Si está vacío, no se carga (degradación
   * elegante). Se carga SOLO tras consentimiento de analítica (igual que GA4).
   */
  googleAdsId: string;
  /**
   * Dominio del Hosted UI de Cognito para el login federado (Google/Apple), p.
   * ej. `saands-auth.auth.us-east-1.amazoncognito.com`. No es secreto. Si está
   * vacío, los botones de login social no se muestran (el email+contraseña sigue
   * funcionando por llamadas directas a Cognito). Es el output `HostedUiDomain`
   * del AuthStack.
   */
  cognitoDomain: string;
  /**
   * URI de redirección (callback) registrada en el App client de Cognito para
   * el OAuth code grant. Debe coincidir EXACTAMENTE con una de las `callbackUrls`
   * del client. Por defecto, la ruta `/auth/callback` del origen actual.
   */
  oauthRedirectUri: string;
  /** ¿Mostrar el botón "Continuar con Google"? (su IdP debe existir en Cognito). */
  enableGoogleLogin: boolean;
  /** ¿Mostrar el botón "Continuar con Apple"? (su IdP debe existir en Cognito). */
  enableAppleLogin: boolean;
}

function envOr(key: string, fallback: string): string {
  const value = import.meta.env[key as keyof ImportMetaEnv] as string | undefined;
  return value && value.trim() !== '' ? value : fallback;
}

/** Config de la app (con valores por defecto para desarrollo local). */
export const config: AppConfig = {
  apiUrl: envOr('VITE_API_URL', 'http://localhost:3000'),
  cognitoRegion: envOr('VITE_COGNITO_REGION', 'us-east-1'),
  cognitoUserPoolId: envOr('VITE_COGNITO_USER_POOL_ID', ''),
  cognitoClientId: envOr('VITE_COGNITO_CLIENT_ID', ''),
  stripePublishableKey: envOr('VITE_STRIPE_PUBLISHABLE_KEY', ''),
  gaMeasurementId: envOr('VITE_GA_MEASUREMENT_ID', ''),
  contentsquareTagId: envOr('VITE_CONTENTSQUARE_TAG_ID', ''),
  // Id de Google Ads (medición de conversiones/remarketing). Valor por defecto
  // real horneado para que un build sin env no lo pierda (igual que GA4).
  googleAdsId: envOr('VITE_GOOGLE_ADS_ID', 'AW-18525940959'),
  cognitoDomain: envOr('VITE_COGNITO_DOMAIN', ''),
  // Por defecto, la ruta /auth/callback del origen actual (coincide con las
  // callbackUrls del client: https://saands.co/auth/callback y localhost dev).
  oauthRedirectUri: envOr(
    'VITE_OAUTH_REDIRECT_URI',
    typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '',
  ),
  // Flags por proveedor social: el botón solo se muestra si su IdP está
  // realmente desplegado en Cognito. Google activo; Apple apagado hasta tener
  // las credenciales del Apple Developer Program (evita un botón que falla).
  enableGoogleLogin: envOr('VITE_ENABLE_GOOGLE', 'true') !== 'false',
  enableAppleLogin: envOr('VITE_ENABLE_APPLE', 'false') === 'true',
};

/** ¿Hay configuración de Cognito? (si no, la app usa el modo de sesión local). */
export function hasCognitoConfig(): boolean {
  return config.cognitoUserPoolId !== '' && config.cognitoClientId !== '';
}

/**
 * ¿Está configurado el login federado (Hosted UI de Cognito)? Requiere dominio
 * del Hosted UI y que al menos un proveedor esté habilitado. Si no, se oculta
 * toda la sección social y solo se ofrece email+contraseña.
 */
export function hasFederatedLogin(): boolean {
  return (
    config.cognitoDomain !== '' &&
    config.cognitoClientId !== '' &&
    (config.enableGoogleLogin || config.enableAppleLogin)
  );
}
